import { createContext, useContext, useEffect, useState } from 'react';
import { api, setToken, clearToken, setViewAs, getViewAsId } from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('sss_crm_token');
    if (!token) { setLoading(false); return; }
    api.me().then((me) => {
      // A persisted view-as id that didn't actually put us in preview mode is
      // stale (e.g. a startViewAs whose /me never completed). Clear it so every
      // request stops silently impersonating and 403ing admin-only routes.
      if (getViewAsId() && !me.is_preview) setViewAs(null);
      setUser(me);
    }).catch(() => clearToken()).finally(() => setLoading(false));
  }, []);

  const login = async (username, password, website = '') => {
    const { token, user } = await api.login(username, password, website);
    setToken(token);
    setUser(user);
    return user;
  };

  const logout = () => { clearToken(); setViewAs(null); setUser(null); };

  // Begin impersonating an account manager. Admin-only client-side gate;
  // server enforces the same. Refetches /auth/me which now returns the
  // previewed user's record + actual_user metadata.
  const startViewAs = async (userId) => {
    setViewAs(userId);
    try {
      const me = await api.me();
      setUser(me);
    } catch (e) {
      setViewAs(null); // a failed /me must not leave a stale impersonation id behind
      throw e;
    }
  };
  const exitViewAs = async () => {
    setViewAs(null);
    const me = await api.me();
    setUser(me);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser, startViewAs, exitViewAs }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// "Admin-like" considers the ACTUAL user, not the previewed one -- so admin
// previewing as a rep doesn't keep showing admin-only surfaces by accident.
// Inside preview mode, user.role is the previewed role and gates the UI as
// that role would experience it.
export const isAdminLike = (user) => user && (user.role === 'admin' || user.role === 'leadership');
